import { useEffect, useMemo } from 'react'
import { useLocation } from 'react-router'
import { replaceWithRegion } from 'actions/NavigationActions'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { useTypeaheadExtractionRefresh } from './useTypeaheadExtractionRefresh'
import {
  queryKeyAdults,
  queryKeyAmenities,
  queryKeyCheckIn,
  queryKeyCheckOut,
  queryKeyChildren,
  queryKeyDestinationId,
  queryKeyExtracting,
  queryKeyFlexibleMonths,
  queryKeyFlexibleNights,
  queryKeyInclusions,
  queryKeyTags,
  queryKeyChains,
  chainsDelimiter,
  queryKeySubChains,
  subChainsDelimiter,
  queryKeyLandmarkId,
  queryKeyLat,
  queryKeyLng,
  queryKeyPriceGte,
  queryKeyPriceLte,
  queryKeyPropertyId,
  queryKeySortBy,
  queryKeyPages,
  queryKeyQuery,
  queryKeyTypeaheadId,
} from 'constants/url'
import { queryNullChildren, ANYWHERE_PLACE_ID } from 'constants/search'
import { deleteSearchParams, setManySearchParamValues } from 'lib/url/searchUrlUtils'
import { searchQueryParams } from 'lib/search/searchUtils'
import useTypeaheadRollout from 'hooks/Search/useTypeaheadRollout'

/**
 * Maps extracted typeahead streaming data to URL search parameters.
 * Uses mapStreamingDataToExtractedParams for extraction, then applies user preference overrides.
 * @param streamingData The streaming data containing extracted information
 * @param query The natural language query string to use for name fields
 * @param currentSearch The current URL search string (to preserve user-entered values)
 * @returns Array of URL parameters to set
 */
function mapExtractedDataToUrlParams(
  streamingData: App.TypeaheadStreamingData,
  currentSearch: string,
): Array<{ paramName: string; paramValue: string | number | undefined }> {
  const paramsToSet: Array<{ paramName: string; paramValue: string | number | undefined }> = []
  const currentParams = new URLSearchParams(currentSearch)

  // Get extracted data from finalSearchItem if available
  const searchItem = streamingData.finalSearchItem?.data
  const extractedData = searchItem?.searchType === 'searchItemV3' ? searchItem.searchItem.extractedData : undefined

  // If no extracted data, set defaults and return
  if (!extractedData) {
    paramsToSet.push({ paramName: queryKeyDestinationId, paramValue: ANYWHERE_PLACE_ID })
    paramsToSet.push({ paramName: queryKeyAdults, paramValue: '2' })
    paramsToSet.push({ paramName: queryKeyChildren, paramValue: queryNullChildren })
    paramsToSet.push({ paramName: queryKeyExtracting, paramValue: undefined })
    return paramsToSet
  }

  // Location params based on searchType
  const { searchType } = extractedData

  if ('property' in extractedData && extractedData.property) {
    paramsToSet.push({ paramName: queryKeyPropertyId, paramValue: extractedData.property.propertyId })
  } else if (searchType === 'latLong' && extractedData.lat && extractedData.lng) {
    paramsToSet.push({ paramName: queryKeyLat, paramValue: extractedData.lat })
    paramsToSet.push({ paramName: queryKeyLng, paramValue: extractedData.lng })
  } else if (searchType === 'landmark' && extractedData.placeIds && extractedData.placeIds.length > 0) {
    paramsToSet.push({ paramName: queryKeyLandmarkId, paramValue: extractedData.placeIds.join(',') })
  } else if (searchType === 'destination' && extractedData.placeIds && extractedData.placeIds.length > 0) {
    paramsToSet.push({ paramName: queryKeyDestinationId, paramValue: extractedData.placeIds.join(',') })
  } else {
    paramsToSet.push({ paramName: queryKeyDestinationId, paramValue: ANYWHERE_PLACE_ID })
  }
  if (extractedData.query) {
    paramsToSet.push({ paramName: queryKeyQuery, paramValue: extractedData.query })
  }

  // Travel dates - user-entered values ALWAYS take precedence over extracted values
  const userCheckIn = currentParams.get(queryKeyCheckIn)
  const userCheckOut = currentParams.get(queryKeyCheckOut)
  const userFlexibleNights = currentParams.get(queryKeyFlexibleNights)
  const userFlexibleMonths = currentParams.get(queryKeyFlexibleMonths)
  const hasUserDates = userCheckIn || userCheckOut || userFlexibleNights || userFlexibleMonths

  if (hasUserDates) {
    if (userCheckIn) paramsToSet.push({ paramName: queryKeyCheckIn, paramValue: userCheckIn })
    if (userCheckOut) paramsToSet.push({ paramName: queryKeyCheckOut, paramValue: userCheckOut })
    if (userFlexibleNights) paramsToSet.push({ paramName: queryKeyFlexibleNights, paramValue: userFlexibleNights })
    if (userFlexibleMonths) paramsToSet.push({ paramName: queryKeyFlexibleMonths, paramValue: userFlexibleMonths })
  } else {
    if (extractedData.checkIn) paramsToSet.push({ paramName: queryKeyCheckIn, paramValue: extractedData.checkIn })
    if (extractedData.checkOut) paramsToSet.push({ paramName: queryKeyCheckOut, paramValue: extractedData.checkOut })
    if (extractedData.flexibleNights) paramsToSet.push({ paramName: queryKeyFlexibleNights, paramValue: extractedData.flexibleNights })
    if (extractedData.flexibleMonths) paramsToSet.push({ paramName: queryKeyFlexibleMonths, paramValue: extractedData.flexibleMonths })
  }

  // Occupancy - extracted values take precedence, fallback to user-entered values
  const userAdults = currentParams.get(queryKeyAdults)
  const userChildren = currentParams.get(queryKeyChildren)

  if (extractedData.occupancy && extractedData.occupancy.length > 0) {
    const adults = extractedData.occupancy.map(room => room.adults).join(',')
    const children = extractedData.occupancy.map(room =>
      room.childrenAge && room.childrenAge.length > 0 ? room.childrenAge.join(';') : 'none',
    ).join(',')
    paramsToSet.push({ paramName: queryKeyAdults, paramValue: adults })
    paramsToSet.push({ paramName: queryKeyChildren, paramValue: children })
  } else if (userAdults) {
    // Preserve user's selection if extraction returned no occupancy
    paramsToSet.push({ paramName: queryKeyAdults, paramValue: userAdults })
    if (userChildren) paramsToSet.push({ paramName: queryKeyChildren, paramValue: userChildren })
  }

  // Tags - user-applied values take precedence
  const userTags = currentParams.get(queryKeyTags)
  if (userTags) {
    paramsToSet.push({ paramName: queryKeyTags, paramValue: userTags })
  } else if (extractedData.tags && extractedData.tags.length > 0) {
    paramsToSet.push({ paramName: queryKeyTags, paramValue: extractedData.tags.join(',') })
  }

  // Chains - user-applied values take precedence
  const userChains = currentParams.get(queryKeyChains)
  if (userChains) {
    paramsToSet.push({ paramName: queryKeyChains, paramValue: userChains })
  } else if (extractedData.chains && extractedData.chains.length > 0) {
    paramsToSet.push({ paramName: queryKeyChains, paramValue: extractedData.chains.join(chainsDelimiter) })
  }

  // SubChains - user-applied values take precedence
  const userSubChains = currentParams.get(queryKeySubChains)
  if (userSubChains) {
    paramsToSet.push({ paramName: queryKeySubChains, paramValue: userSubChains })
  } else if (extractedData.subChains && extractedData.subChains.length > 0) {
    paramsToSet.push({ paramName: queryKeySubChains, paramValue: extractedData.subChains.join(subChainsDelimiter) })
  }

  // Inclusions - user-applied values take precedence
  const userInclusions = currentParams.get(queryKeyInclusions)
  if (userInclusions) {
    paramsToSet.push({ paramName: queryKeyInclusions, paramValue: userInclusions })
  } else if (extractedData.inclusions && extractedData.inclusions.length > 0) {
    paramsToSet.push({ paramName: queryKeyInclusions, paramValue: extractedData.inclusions.join(',') })
  }

  // Amenities - user-applied values take precedence
  const userAmenities = currentParams.get(queryKeyAmenities)
  if (userAmenities) {
    paramsToSet.push({ paramName: queryKeyAmenities, paramValue: userAmenities })
  } else if (extractedData.amenities && extractedData.amenities.length > 0) {
    paramsToSet.push({ paramName: queryKeyAmenities, paramValue: extractedData.amenities.join(',') })
  }

  // SortBy - user-applied values take precedence
  const userSortBy = currentParams.get(queryKeySortBy)
  if (userSortBy) {
    paramsToSet.push({ paramName: queryKeySortBy, paramValue: userSortBy })
  } else if (extractedData.sortBy) {
    paramsToSet.push({ paramName: queryKeySortBy, paramValue: extractedData.sortBy })
  }

  // Price filters - user-applied values take precedence
  const userPriceGte = currentParams.get(queryKeyPriceGte)
  const userPriceLte = currentParams.get(queryKeyPriceLte)
  if (userPriceGte || userPriceLte) {
    if (userPriceGte) paramsToSet.push({ paramName: queryKeyPriceGte, paramValue: userPriceGte })
    if (userPriceLte) paramsToSet.push({ paramName: queryKeyPriceLte, paramValue: userPriceLte })
  } else {
    if (extractedData.priceGte) paramsToSet.push({ paramName: queryKeyPriceGte, paramValue: extractedData.priceGte })
    if (extractedData.priceLte) paramsToSet.push({ paramName: queryKeyPriceLte, paramValue: extractedData.priceLte })
  }

  // Remove extracting=true param
  paramsToSet.push({ paramName: queryKeyExtracting, paramValue: undefined })

  return paramsToSet
}

interface UseTypeaheadExtractionUrlUpdateParams {
  extracting: string | undefined
  typeaheadId: string | undefined
  query: string | undefined
  windowSearch: string
  pathname: string
  typeaheadTypes?: Array<App.SearchPlaceType>
}

interface UseTypeaheadExtractionUrlUpdateReturn {
  isExtracting: boolean
  isExtractionComplete: boolean
  isSearchBlocked: boolean
}

/**
 * Hook to handle typeahead extraction URL updates
 * - Checks if extraction is in progress
 * - Waits for extraction to complete
 * - Maps extracted data to URL params
 * - Updates URL and removes extracting=true when complete
 * - Blocks search while extraction is in progress
 */
export function useTypeaheadExtractionUrlUpdate({
  extracting,
  typeaheadId,
  query,
  windowSearch,
  pathname,
  typeaheadTypes,
}: UseTypeaheadExtractionUrlUpdateParams): UseTypeaheadExtractionUrlUpdateReturn {
  const dispatch = useAppDispatch()
  const location = useLocation()
  const streamingData = useAppSelector(state =>
    typeaheadId ? state.search.typeaheadStreamingData[typeaheadId] : undefined,
  )
  const typeaheadRollout = useTypeaheadRollout()

  const isExtracting = extracting === 'true'

  // Handle page refresh during extraction - re-dispatch streamTypeahead if needed
  useTypeaheadExtractionRefresh({
    isExtracting,
    typeaheadId,
    query: query,
    typeaheadTypes,
    rollout: typeaheadRollout,
  })

  // Check if extraction is complete
  const isExtractionComplete = useMemo(() => {
    if (!isExtracting || !streamingData || !query) {
      return false
    }
    return streamingData.isComplete && streamingData.completedInput === query
  }, [isExtracting, streamingData, query])

  // Update URL with extracted data when extraction completes
  useEffect(() => {
    if (!isExtractionComplete || !streamingData || !typeaheadId) {
      return
    }

    // Map extracted data to URL params (pass current search to preserve user-entered values)
    const paramsToSet = mapExtractedDataToUrlParams(streamingData, windowSearch)

    // Preserve the current typeaheadId - it should stay in URL after extraction completes
    paramsToSet.push({ paramName: queryKeyTypeaheadId, paramValue: typeaheadId })

    // Clear all search params first (like a new search would)
    const clearedSearch = deleteSearchParams(windowSearch, ...searchQueryParams, queryKeyPages)

    // Set only the extracted params (including the preserved typeaheadId)
    const newSearch = setManySearchParamValues(clearedSearch, paramsToSet)

    // Preserve the current hash (e.g., #search_hotels) to keep the mobile search drawer open
    const currentHash = location.hash ? location.hash.slice(1) : undefined

    dispatch(replaceWithRegion(pathname, newSearch, currentHash))
  }, [isExtractionComplete, streamingData, typeaheadId, windowSearch, pathname, dispatch, query, location.hash])

  // Block search while extraction is in progress
  const isSearchBlocked = isExtracting && !isExtractionComplete

  return {
    isExtracting,
    isExtractionComplete,
    isSearchBlocked,
  }
}
